import { useEffect, useState } from "react";

// True when the app is running inside a Capacitor native shell
// (iOS or Android), false on the browser web build.
//
// Sync read at first render. Capacitor injects window.Capacitor
// during its bootstrap, so on the web build window.Capacitor is
// undefined and isNativePlatform returns false.
function checkIsNative(): boolean {
  // Avoid SSR breakage — this app is SPA so window always exists,
  // but the guard is cheap and future-proofs against import-time
  // evaluation.
  if (typeof window === "undefined") return false;
  const cap = (window as unknown as {
    Capacitor?: { isNativePlatform?: () => boolean };
  }).Capacitor;
  return cap?.isNativePlatform?.() === true;
}

export const isNativePlatform = checkIsNative();

// React hook variant. Today the value is static for the session
// (no way to switch from web to native without a reload), so this
// just returns the precomputed boolean. Wrap in useEffect so the
// SSR / first-render path can't crash on missing window.
export function useIsNativePlatform(): boolean {
  const [native, setNative] = useState<boolean>(false);
  useEffect(() => setNative(checkIsNative()), []);
  return native;
}

// Mobile-viewport breakpoint. Matches Tailwind's `md` at 768px, so
// anything the app already styles with `md:` breakpoints stays in
// visual sync with our JavaScript-side design switch.
const MOBILE_MAX_PX = 767;

function checkIsNarrow(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`).matches;
}

// Native / mobile design gate. Returns true whenever the app is
// rendering onto a mobile-sized surface — either the Capacitor iOS
// shell (always) or a browser viewport under the md breakpoint.
// Desktop browser stays on the classic (data-dense) design.
//
// Live-updating: hooked into a matchMedia listener so rotating the
// device or resizing the browser flips the layout without a reload.
// Was previously an opt-in beta flag; graduated to default in the
// commit that dropped IOSDesignPanel from Settings.
export function useMobileDesign(): boolean {
  const native = useIsNativePlatform();
  const [narrow, setNarrow] = useState<boolean>(checkIsNarrow);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`);
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches);
    // matchMedia has two listener APIs — addEventListener is the
    // modern one; older Safari (<14) only supports the deprecated
    // addListener. Try modern first, fall back if unavailable.
    if ("addEventListener" in mq) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    (mq as unknown as { addListener: (l: (e: MediaQueryListEvent) => void) => void })
      .addListener(handler);
    return () => {
      (mq as unknown as { removeListener: (l: (e: MediaQueryListEvent) => void) => void })
        .removeListener(handler);
    };
  }, []);

  return native || narrow;
}

// Back-compat alias. Every existing caller (Layout, Schedule,
// NotificationBell, RequestList) imports useBetaDesign; keep the
// name to avoid a broad rename in the same commit. New code should
// prefer useMobileDesign for clarity.
export const useBetaDesign = useMobileDesign;
